import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';

console.log('API Client initialized with URL:', API_URL);

// Create axios instance
const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    console.log('Making request to:', config.baseURL + config.url);
    if (config.skipAuth) {
      delete config.headers.Authorization;
      return config;
    }
    const authData = localStorage.getItem('authData');
    if (authData) {
      const { token } = JSON.parse(authData);
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => {
    console.error('Request error:', error);
    return Promise.reject(error);
  }
);

// Single-flight refresh: concurrent 401s share one refresh request
let refreshPromise = null;

const clearSessionAndRedirect = (reason) => {
  localStorage.removeItem('authData');
  localStorage.setItem('loginReason', reason || 'Your session has expired. Please log in again.');
  window.location.href = '/login';
};

const refreshAccessToken = () => {
  if (!refreshPromise) {
    const authData = localStorage.getItem('authData');
    const refreshToken = authData ? JSON.parse(authData).refreshToken : null;

    if (!refreshToken) {
      return Promise.reject(new Error('No refresh token available'));
    }

    // Plain axios call bypasses apiClient interceptors to avoid recursion
    refreshPromise = axios
      .post(`${API_URL}/auth/refresh-token`, { refreshToken })
      .then((response) => {
        const { accessToken, refreshToken: newRefreshToken, user } = response.data.data;
        const stored = localStorage.getItem('authData');
        const storedData = stored ? JSON.parse(stored) : {};
        localStorage.setItem('authData', JSON.stringify({ ...storedData, user: user || storedData.user, token: accessToken, refreshToken: newRefreshToken }));
        return accessToken;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
};

// Response interceptor to handle errors
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalConfig = error.config;

    if (error.response?.status !== 401 || originalConfig?.skipAuthRedirect) {
      return Promise.reject(error);
    }

    // Already retried once — session is genuinely invalid
    if (originalConfig.authRetry) {
      clearSessionAndRedirect(error.response?.data?.error?.message);
      return Promise.reject(error);
    }

    try {
      const newToken = await refreshAccessToken();
      originalConfig.authRetry = true;
      originalConfig.headers = originalConfig.headers || {};
      originalConfig.headers.Authorization = `Bearer ${newToken}`;
      return apiClient(originalConfig);
    } catch (refreshError) {
      const reason = refreshError.response?.data?.error?.message || error.response?.data?.error?.message;
      clearSessionAndRedirect(reason);
      return Promise.reject(error);
    }
  }
);

// Authentication API
export const authAPI = {
  register: (userData) => apiClient.post('/auth/register', userData),
  login: (email, password, otpChannel = 'email') => apiClient.post('/auth/login', { email, password, otpChannel }),
  adminLogin: (email, password) => apiClient.post('/auth/admin/login', { email, password }),
  verifyOTP: (userId, otp, purpose = 'login') => apiClient.post('/auth/verify-otp', { userId, code: otp, purpose }),
  resendOTP: (userId, purpose = 'login', otpChannel = 'email') => apiClient.post('/auth/resend-otp', { userId, purpose, otpChannel }),
  logout: () => apiClient.post('/auth/logout'),
  getCurrentUser: () => apiClient.get('/auth/me'),
  verifyPassword: (data) => apiClient.post('/auth/verify-password', data),
  forgotPassword: (email) => apiClient.post('/auth/forgot-password', { email }),
  resetPassword: (token, newPassword) => apiClient.post('/auth/reset-password', { token, newPassword }),
};

// Users API
export const usersAPI = {
  getAll: (filters = {}) => {
    const params = new URLSearchParams();
    // Only add filters if they have actual values (not empty strings)
    if (filters.userType && filters.userType !== '') params.append('userType', filters.userType);
    if (filters.status && filters.status !== '') params.append('status', filters.status);
    if (filters.isActive !== undefined && filters.isActive !== '') params.append('isActive', filters.isActive);
    if (filters.search && filters.search !== '') params.append('search', filters.search);
    return apiClient.get(`/users?${params.toString()}`);
  },
  createUser: (userData) => apiClient.post('/users/create', userData),
  getById: (id) => apiClient.get(`/users/${id}`),
  create: (userData) => apiClient.post('/users', userData),
  update: (id, userData) => apiClient.put(`/users/${id}`, userData),
  delete: (id) => apiClient.delete(`/users/${id}`),
  activate: (id) => apiClient.patch(`/users/${id}/activate`),
  deactivate: (id) => apiClient.patch(`/users/${id}/deactivate`),
  suspend: (id, reason) => apiClient.patch(`/users/${id}/suspend`, { reason }),
  getStatistics: () => apiClient.get('/users/statistics'),
};

// Providers API
export const providersAPI = {
  getAll: (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.search) params.append('search', filters.search);
    return apiClient.get(`/providers?${params.toString()}`);
  },
  getById: (id) => apiClient.get(`/providers/${id}`),
  approve: (id) => apiClient.patch(`/providers/${id}/approve`),
  reject: (id, reason) => apiClient.patch(`/providers/${id}/reject`, { reason }),
  suspend: (id, reason) => apiClient.patch(`/providers/${id}/suspend`, { reason }),
  activate: (id) => apiClient.patch(`/providers/${id}/activate`),
};

// Bookings API
export const bookingsAPI = {
  getAll: (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.search) params.append('search', filters.search);
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    return apiClient.get(`/bookings?${params.toString()}`);
  },
  getById: (id) => apiClient.get(`/bookings/${id}`),
  updateStatus: (id, status) => apiClient.patch(`/bookings/${id}/status`, { status }),
};

// Family Groups API
export const familyAPI = {
  getAll: (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.search) params.append('search', filters.search);
    if (filters.isActive !== undefined) params.append('isActive', filters.isActive);
    if (filters.page) params.append('page', filters.page);
    if (filters.limit) params.append('limit', filters.limit);
    return apiClient.get(`/families/all?${params.toString()}`);
  },
  getMy: () => apiClient.get('/families/my'),
  getMyFamily: () => apiClient.get('/families/my'),
  getById: (id) => apiClient.get(`/families/${id}`),
  create: (familyData) => apiClient.post('/families', familyData),
  update: (id, familyData) => apiClient.put(`/families/${id}`, familyData),
  delete: (id) => apiClient.delete(`/families/${id}`),
  addMember: (id, memberData) => apiClient.post(`/families/${id}/members`, memberData),
  updateMember: (id, memberId, permissions) => apiClient.put(`/families/${id}/members/${memberId}`, permissions),
  removeMember: (id, memberId) => apiClient.delete(`/families/${id}/members/${memberId}`),
  searchByCode: (code) => apiClient.get(`/families/search/${code}`),
  addFamilyMember: (memberData) => apiClient.post('/families/members/new', memberData),
};

// Provider Organizations API
export const providerOrgAPI = {
  getAll: (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.search) params.append('search', filters.search);
    if (filters.isActive !== undefined) params.append('isActive', filters.isActive);
    if (filters.isVerified !== undefined) params.append('isVerified', filters.isVerified);
    if (filters.page) params.append('page', filters.page);
    if (filters.limit) params.append('limit', filters.limit);
    return apiClient.get(`/provider-organizations/all?${params.toString()}`);
  },
  getMy: () => apiClient.get('/provider-organizations/my'),
  getMyOrganization: () => apiClient.get('/provider-organizations/my'),
  getById: (id) => apiClient.get(`/provider-organizations/${id}`),
  create: (orgData) => apiClient.post('/provider-organizations', orgData),
  update: (id, orgData) => apiClient.put(`/provider-organizations/${id}`, orgData),
  delete: (id) => apiClient.delete(`/provider-organizations/${id}`),
  verify: (id) => apiClient.patch(`/provider-organizations/${id}/verify`),
  addSubuser: (id, subuserData) => apiClient.post(`/provider-organizations/${id}/subusers`, subuserData),
  updateSubuser: (id, subuserId, permissions) => apiClient.put(`/provider-organizations/${id}/subusers/${subuserId}`, permissions),
  removeSubuser: (id, subuserId) => apiClient.delete(`/provider-organizations/${id}/subusers/${subuserId}`),
  searchByCode: (code) => apiClient.get(`/provider-organizations/search/${code}`),
  addProviderSubuser: (subuserData) => apiClient.post('/provider-organizations/subusers/new', subuserData),
};

// Provider Profile API
export const providerProfileAPI = {
  getMyProfile: () => apiClient.get('/provider-profiles/my-profile'),
  getProfile: (providerId) => apiClient.get(`/provider-profiles/${providerId}`),
  updateProfile: (providerId, profileData) => apiClient.put(`/provider-profiles/${providerId}`, profileData),

  // Documents
  addDocument: (providerId, documentData) => apiClient.post(`/provider-profiles/${providerId}/documents`, documentData),
  updateDocument: (providerId, documentId, documentData) => apiClient.put(`/provider-profiles/${providerId}/documents/${documentId}`, documentData),
  deleteDocument: (providerId, documentId) => apiClient.delete(`/provider-profiles/${providerId}/documents/${documentId}`),

  // Certifications
  addCertification: (providerId, certData) => apiClient.post(`/provider-profiles/${providerId}/certifications`, certData),
  updateCertification: (providerId, certId, certData) => apiClient.put(`/provider-profiles/${providerId}/certifications/${certId}`, certData),
  deleteCertification: (providerId, certId) => apiClient.delete(`/provider-profiles/${providerId}/certifications/${certId}`),

  // Education
  addEducation: (providerId, eduData) => apiClient.post(`/provider-profiles/${providerId}/education`, eduData),
  updateEducation: (providerId, eduId, eduData) => apiClient.put(`/provider-profiles/${providerId}/education/${eduId}`, eduData),
  deleteEducation: (providerId, eduId) => apiClient.delete(`/provider-profiles/${providerId}/education/${eduId}`),

  // Work Experience
  addWorkExperience: (providerId, workData) => apiClient.post(`/provider-profiles/${providerId}/work-experience`, workData),
  updateWorkExperience: (providerId, workId, workData) => apiClient.put(`/provider-profiles/${providerId}/work-experience/${workId}`, workData),
  deleteWorkExperience: (providerId, workId) => apiClient.delete(`/provider-profiles/${providerId}/work-experience/${workId}`),

  // References
  addReference: (providerId, refData) => apiClient.post(`/provider-profiles/${providerId}/references`, refData),
  updateReference: (providerId, refId, refData) => apiClient.put(`/provider-profiles/${providerId}/references/${refId}`, refData),
  deleteReference: (providerId, refId) => apiClient.delete(`/provider-profiles/${providerId}/references/${refId}`),

  // Admin
  reviewProfile: (providerId, reviewData) => apiClient.put(`/provider-profiles/${providerId}/review`, reviewData),
  verifyDocument: (documentId, verificationData) => apiClient.put(`/provider-profiles/documents/${documentId}/verify`, verificationData),
};

// Provider Products API
export const providerProductAPI = {
  // Public
  getCategories: () => apiClient.get('/provider-products/categories'),
  getProviderProducts: (providerId, params) => apiClient.get(`/provider-products/provider/${providerId}`, { params }),
  getProduct: (productId) => apiClient.get(`/provider-products/${productId}`),

  // My Products (authenticated)
  getMyProducts: (params) => apiClient.get('/provider-products/my/all', { params }),
  getMyStats: () => apiClient.get('/provider-products/my/stats'),
  getLowStock: () => apiClient.get('/provider-products/my/low-stock'),
  createProduct: (productData) => apiClient.post('/provider-products/my/create', productData),
  updateProduct: (productId, productData) => apiClient.put(`/provider-products/my/${productId}`, productData),
  deleteProduct: (productId) => apiClient.delete(`/provider-products/my/${productId}`),
  updateStock: (productId, stockData) => apiClient.patch(`/provider-products/my/${productId}/stock`, stockData),
};

// Corporate Organizations API
export const corporateOrgAPI = {
  getByCode: (code) => apiClient.get(`/corporate-organizations/code/${code}`),
  getMyOrganization: () => apiClient.get('/corporate-organizations/my-organization'),
  getOrganization: (id) => apiClient.get(`/corporate-organizations/${id}`),
  getOrganizationStats: (id) => apiClient.get(`/corporate-organizations/${id}/stats`),
  getWellnessAnalytics: (id, params = {}) => apiClient.get(`/corporate-organizations/${id}/wellness-analytics`, { params }),

  // Admins
  getOrganizationAdmins: (id) => apiClient.get(`/corporate-organizations/${id}/admins`),
  addOrganizationAdmin: (id, adminData) => apiClient.post(`/corporate-organizations/${id}/admins`, adminData),
  updateOrganizationAdmin: (id, adminId, adminData) => apiClient.put(`/corporate-organizations/${id}/admins/${adminId}`, adminData),
  deleteOrganizationAdmin: (id, adminId) => apiClient.delete(`/corporate-organizations/${id}/admins/${adminId}`),

  // Members
  getOrganizationMembers: (id, filters = {}) => {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.search) params.append('search', filters.search);
    return apiClient.get(`/corporate-organizations/${id}/members?${params.toString()}`);
  },
  addOrganizationMember: (id, memberData) => apiClient.post(`/corporate-organizations/${id}/members`, memberData),
  updateOrganizationMember: (id, memberId, memberData) => apiClient.put(`/corporate-organizations/${id}/members/${memberId}`, memberData),
  deleteOrganizationMember: (id, memberId) => apiClient.delete(`/corporate-organizations/${id}/members/${memberId}`),
  approveMember: (id, memberId) => apiClient.post(`/corporate-organizations/${id}/members/${memberId}/approve`),
  rejectMember: (id, memberId) => apiClient.post(`/corporate-organizations/${id}/members/${memberId}/reject`),
  suspendMember: (id, memberId) => apiClient.post(`/corporate-organizations/${id}/members/${memberId}/suspend`),
  reactivateMember: (id, memberId) => apiClient.post(`/corporate-organizations/${id}/members/${memberId}/reactivate`),

  // Bulk Upload
  downloadMemberTemplate: () => {
    return apiClient.get('/corporate-organizations/member-template', {
      responseType: 'blob',
    });
  },
  bulkUploadMembers: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post(`/corporate-organizations/${id}/members/bulk-upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  // Provider Links
  getLinkedProviders: (id) => apiClient.get(`/corporate-organizations/${id}/providers`),
  searchProviders: (id, query) => apiClient.get(`/corporate-organizations/${id}/providers/search`, { params: { q: query } }),
  addProviderLink: (id, linkData) => apiClient.post(`/corporate-organizations/${id}/providers`, linkData),
  updateProviderLink: (id, linkId, linkData) => apiClient.put(`/corporate-organizations/${id}/providers/${linkId}`, linkData),
  removeProviderLink: (id, linkId) => apiClient.delete(`/corporate-organizations/${id}/providers/${linkId}`),
};

// Provider API
export const providerAPI = {
  getAll: (params) => apiClient.get('/provider-organizations/active', { params }),
  getById: (providerId) => apiClient.get(`/provider-organizations/${providerId}`),
  searchByCode: (code) => apiClient.get(`/provider-organizations/search/${code}`),
};

export const careRequestAPI = {
  list: (params) => apiClient.get('/care-requests', { params }),
  get: (id) => apiClient.get(`/care-requests/${id}`),
  create: (data) => apiClient.post('/care-requests', data),
  update: (id, data) => apiClient.put(`/care-requests/${id}`, data),
  cancel: (id, reason) => apiClient.delete(`/care-requests/${id}`, { data: { reason } }),
  matches: (id) => apiClient.get(`/care-requests/${id}/matches`),
  selectProvider: (id, data) => apiClient.post(`/care-requests/${id}/select-provider`, data),
  guestMatches: (id) => apiClient.get(`/care-requests/guest/${id}/matches`),
  enhanceText: (text) => apiClient.post('/care-requests/enhance-text', { text }, { skipAuth: true, skipAuthRedirect: true }),
};

// Wellness Booking API
export const bookingAPI = {
  // Provider calendar and availability
  getProviderCalendar: (providerId, params) => apiClient.get(`/wellness-bookings/providers/${providerId}/calendar`, { params }),
  setWeeklyAvailability: (providerId, data) => apiClient.post(`/wellness-bookings/providers/${providerId}/weekly-availability`, data),
  createCalendarBlock: (providerId, data) => apiClient.post(`/wellness-bookings/providers/${providerId}/calendar-blocks`, data),
  getCalendarBlocks: (providerId) => apiClient.get(`/wellness-bookings/providers/${providerId}/calendar-blocks`),
  deleteCalendarBlock: (providerId, blockId) => apiClient.delete(`/wellness-bookings/providers/${providerId}/calendar-blocks/${blockId}`),

  // Personal bookings
  getPersonalBookings: (providerId) => apiClient.get(`/wellness-bookings/providers/${providerId}/personal-bookings`),
  createPersonalBooking: (providerId, data) => apiClient.post(`/wellness-bookings/providers/${providerId}/personal-bookings`, data),
  deletePersonalBooking: (providerId, bookingId) => apiClient.delete(`/wellness-bookings/providers/${providerId}/personal-bookings/${bookingId}`),

  // Provider bookings
  getProviderBookings: (providerId, params) => apiClient.get(`/wellness-bookings/providers/${providerId}/bookings`, { params }),
  getBookingDetails: (bookingId) => apiClient.get(`/wellness-bookings/bookings/${bookingId}`),
  updateBookingStatus: (bookingId, data) => apiClient.patch(`/wellness-bookings/bookings/${bookingId}/status`, data),
  updateBookingTime: (bookingId, data) => apiClient.patch(`/wellness-bookings/bookings/${bookingId}/time`, data),
  getAvailableSlots: (providerId, date, duration = 60) => apiClient.get(`/wellness-bookings/providers/${providerId}/available-slots`, { params: { date, duration } }),
  requestReschedule: (bookingId, data) => apiClient.post(`/wellness-bookings/bookings/${bookingId}/reschedule`, data),

  // Member bookings
  getMyBookings: (params) => apiClient.get('/wellness-bookings/bookings/my-bookings', { params }),
  createBooking: (data) => apiClient.post('/wellness-bookings/bookings', data),

  // Questionnaires
  getProviderTemplates: (providerId) => apiClient.get(`/wellness-bookings/providers/${providerId}/questionnaire-templates`),
  createQuestionnaireTemplate: (providerId, data) => apiClient.post(`/wellness-bookings/providers/${providerId}/questionnaire-templates`, data),
  submitQuestionnaireResponse: (data) => apiClient.post('/wellness-bookings/questionnaire-responses', data),
  getBookingResponses: (bookingId) => apiClient.get(`/wellness-bookings/bookings/${bookingId}/questionnaire-responses`),

  // Booking links
  getProviderBookingLinks: (providerId) => apiClient.get(`/wellness-bookings/providers/${providerId}/booking-links`),
  createBookingLink: (providerId, data) => apiClient.post(`/wellness-bookings/providers/${providerId}/booking-links`, data),
  getBookingLinkByCode: (linkCode) => apiClient.get(`/wellness-bookings/booking-links/${linkCode}`),
};

// Member Wellness API (For Corporate Members)
export const memberWellnessAPI = {
  // Dashboard
  getDashboard: () => apiClient.get('/wellness/member/dashboard'),

  // Programs
  getMyPrograms: (params) => apiClient.get('/wellness/member/programs', { params }),
  getAvailablePrograms: () => apiClient.get('/wellness/member/programs/available'),
  enrollInProgram: (programId) => apiClient.post(`/wellness/member/programs/${programId}/enroll`),
  getProgramDetails: (enrollmentId) => apiClient.get(`/wellness/member/programs/${enrollmentId}`),

  // Activities
  logActivity: (enrollmentId, activityData) => apiClient.post(`/wellness/member/programs/${enrollmentId}/activities`, activityData),
  getActivityLogs: (params) => apiClient.get('/wellness/member/activities', { params }),

  // Reports & Assessments
  getReports: (params) => apiClient.get('/wellness/member/reports', { params }),
  getAssessments: (params) => apiClient.get('/wellness/member/assessments', { params }),

  // Weight (for MET-based calorie calculation)
  getMemberWeight: () => apiClient.get('/wellness/member/weight'),
  saveMemberWeight: (weightKg) => apiClient.put('/wellness/member/weight', { weightKg }),
  syncHealthData: (data) => apiClient.post('/wellness/member/health-sync', data),
  getHealthDataHistory: (days = 30) => apiClient.get(`/wellness/member/health-sync?days=${days}`),
};

// Provider Wellness API
export const providerWellnessAPI = {
  getOverview: (params = {}) => apiClient.get('/provider-wellness/overview', { params }),
  getOrganizations: (params = {}) => apiClient.get('/provider-wellness/organizations', { params }),
  getMembers: (params = {}) => apiClient.get('/provider-wellness/members', { params }),
  getComparativeAnalytics: (params = {}) => apiClient.get('/provider-wellness/comparative-analytics', { params }),
  getOrganizationDetails: (organizationId, params = {}) => apiClient.get(`/provider-wellness/organizations/${organizationId}`, { params }),
  getMemberDetails: (memberId, params = {}) => apiClient.get(`/provider-wellness/members/${memberId}`, { params }),
};

export default apiClient;
